import * as React from 'react';

import classNames from 'classnames';
import Mousetrap from 'mousetrap';

import type {User} from 'nutshell-core/types';

import {SidebarUserAvatar} from 'shells/avatar/sidebar-user-avatar';
// @ts-expect-error
import {SidebarIconHelpIcon} from 'shells/icon/svg/sidebar-icon-help';
import type {VideoProgress} from 'shells/video/video-card/video-modal-native';
import {VideoModalNative} from 'shells/video/video-card/video-modal-native';

import {AiFeatureType} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';

import {IntercomLaunchButton} from '../../intercom';
import {LeaveAReviewModal} from '../../leave-a-review/leave-a-review-modal';
import {AircallWorkspaceMount} from '../../phone-call/aircall-workspace-mount';
import {ReferAFriendModal} from '../../refer-a-friend/refer-a-friend-modal';
import {useIsAiFeatureEnabled} from '../../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';
import {getAgentIsOnline} from '../../sidebar/helpers';
import {TeamAiButton} from '../../team-ai';

import {getIconSize} from './helpers';
import {NavbarDemoButton} from './navbar-demo-button';
import {NavbarIcon} from './navbar-icon';
import {NotificationBellButton} from './notification-bell-button';
import {NotificationsPopover} from './notifications/notifications-popover';
import {UserMenuPopover} from './user-menu-popover';

type Props = {
    user: User | null | undefined;
    userInitials: string;
    isSparse?: boolean;
};

const NUTSHELL_IN_NINETY_SECONDS_URL =
    'https://static.nlcdn.com/video/nutshell-in-90s-20260410.mp4';

export function NavbarRightSection(props: Props) {
    const {isSparse = false} = props;

    const [isNotificationsOpened, setIsNotificationsOpened] = React.useState<boolean>(false);
    const [isUserMenuOpened, setIsUserMenuOpened] = React.useState<boolean>(false);
    const [isReferAFriendModalOpen, setIsReferAFriendModalOpen] = React.useState<boolean>(false);
    const [isLeaveAReviewModalOpen, setIsLeaveAReviewModalOpen] = React.useState<boolean>(false);
    const [isDemoVideoModalOpen, setIsDemoVideoModalOpen] = React.useState<boolean>(false);

    const notificationsTabRef = React.useRef(null);
    const userMenuRef = React.useRef(null);

    const isEmailSendingPaused = NutClientConfig.hasEmailSendingPaused;

    const {isEnabled: isTeamAIOutcomeEnabled} = useIsAiFeatureEnabled(
        AiFeatureType.EMAIL_SEQUENCE_WRITER
    );
    const isTeamAIEnabled = NutClientConfig.hasAiOutcomesFF
        ? isTeamAIOutcomeEnabled
        : NutClientConfig.enableAiAgents;

    React.useEffect(() => {
        if (props.user && props.user.hasDisabledShortcuts) {
            return;
        }

        Mousetrap.bind('g n', (e) => {
            e.preventDefault();
            setIsNotificationsOpened(!isNotificationsOpened);
        });

        Mousetrap.bind('g u', (e) => {
            e.preventDefault();
            setIsUserMenuOpened(!isUserMenuOpened);
        });

        return () => {
            Mousetrap.unbind('g n');
            Mousetrap.unbind('g u');
        };
    }, [isNotificationsOpened, isUserMenuOpened, props.user]);

    const navbarRightSectionClasses = classNames({
        'nut-navbar--right-section': !isSparse,
        'nut-navbar-sparse--right-section': isSparse,
        'nut-navbar--right-section-padding': !isTeamAIEnabled, // With the new agent button, we do not need padding to the right
    });

    return (
        <>
            <div className={navbarRightSectionClasses}>
                {props.user && props.user.shouldShowNutshellInNinetySecondsLink && (
                    <NavbarDemoButton onClick={() => setIsDemoVideoModalOpen(true)} />
                )}
                {props.user && props.user.isAircallEnabledForUser && <AircallWorkspaceMount />}
                {/* @ts-expect-error */}
                <IntercomLaunchButton variant='text' className='nut-navbar--button'>
                    <NavbarIcon icon={SidebarIconHelpIcon} size={getIconSize('help')} />
                </IntercomLaunchButton>
                {!isSparse && (
                    <NotificationBellButton
                        isOpen={isNotificationsOpened}
                        size={getIconSize('notification')}
                        onClick={() => setIsNotificationsOpened(!isNotificationsOpened)}
                        getButtonRef={(node) => {
                            // @ts-expect-error
                            notificationsTabRef.current = node;
                        }}
                    />
                )}
                {props.user ? (
                    <button
                        ref={userMenuRef}
                        onClick={() => setIsUserMenuOpened(!isUserMenuOpened)}
                        className='nut-navbar--user-avatar-button'
                    >
                        <SidebarUserAvatar
                            isActive={false}
                            {...props.user}
                            size={getIconSize('email')}
                            isOnline={getAgentIsOnline(props.user)}
                            hasBorder={isUserMenuOpened}
                            statusIndicator={isEmailSendingPaused ? 'warning' : undefined}
                            statusIndicatorTooltip={
                                isEmailSendingPaused ? 'Email sending is paused' : undefined
                            }
                        />
                    </button>
                ) : (
                    <SidebarUserAvatar
                        size={getIconSize('email')}
                        isActive={false}
                        initials={props.userInitials}
                        avatarUrl=''
                    />
                )}
                {isTeamAIEnabled && !isSparse && <TeamAiButton size={getIconSize('robot')} />}
            </div>
            {!isSparse && isNotificationsOpened && (
                <NotificationsPopover
                    anchor={notificationsTabRef.current}
                    onBlur={() => setIsNotificationsOpened(false)}
                    isNotificationsOpened={isNotificationsOpened}
                    setIsNotificationsOpened={setIsNotificationsOpened}
                />
            )}
            {isUserMenuOpened && props.user && (
                <UserMenuPopover
                    // @ts-expect-error
                    loggedInUser={props.user}
                    userMenuRef={userMenuRef.current}
                    setIsUserMenuOpened={setIsUserMenuOpened}
                    variant={isSparse ? 'logout-only' : 'full'}
                    setIsReferAFriendModalOpen={setIsReferAFriendModalOpen}
                    setIsLeaveAReviewModalOpen={setIsLeaveAReviewModalOpen}
                />
            )}
            <ReferAFriendModal
                isOpen={isReferAFriendModalOpen}
                onClose={() => setIsReferAFriendModalOpen(false)}
            />
            {isLeaveAReviewModalOpen && (
                <LeaveAReviewModal onClose={() => setIsLeaveAReviewModalOpen(false)} />
            )}
            {isDemoVideoModalOpen && (
                <VideoModalNative
                    onClose={(progress: VideoProgress | null) => {
                        if (progress) {
                            analyticsClient.track(
                                analyticsClient.EVENTS.TRACK_NUTSHELL_NINETY_SECONDS_VIDEO_CLOSED,
                                {
                                    currentTimeSecs: progress.currentTimeSecs,
                                    percentComplete: progress.percentComplete,
                                }
                            );
                        }
                        setIsDemoVideoModalOpen(false);
                    }}
                    videoUrl={NUTSHELL_IN_NINETY_SECONDS_URL}
                    title='Nutshell in 90 seconds'
                />
            )}
        </>
    );
}
